import { useAppSelector } from 'app/hooks';
import WarningIcon from 'assets/icons/WarningIcon';
import { useChatConntext } from 'components/Hoc/MessageProvider';
import { showConfirmModal } from 'components/Modals/CommonModals';
import useUserDetail from 'hooks/useUserDetail';
import { GiveAwayResponseModel } from 'models/chatMessage';
import React, { useEffect, useState } from 'react';
import ReactConfetti from 'react-confetti';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { platformStates } from 'redux/platformSlice';
import { isPhone } from 'utils/commonUtils';
import parachute from '../../../assets/images/parchute.png';
import { onChangeAuthPage } from '../homeSlice';

interface ParachuteItem {
  id: string;
  giveAwayData: GiveAwayResponseModel;
  x: number;
  delay: number;
  duration: number;
  isClicked: boolean;
  swingOffset: number;
  promotionCode: string;
  showConfetti?: boolean;
}

interface ParachuteItemsProps {
  giveAwayData: GiveAwayResponseModel[];
  onItemClick?: (promotionCode: string) => void;
  onKYCRequired?: () => void;
}

const ParachuteItems: React.FC<ParachuteItemsProps> = ({
  giveAwayData,
  onItemClick,
  onKYCRequired,
}) => {
  const [items, setItems] = useState<ParachuteItem[]>([]);
  const { removeGiveAways } = useChatConntext();
  const { isUserLoggedIn, currentUser } = useUserDetail();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const platform = useAppSelector(platformStates);

  const createParachuteItem = (giveAway: GiveAwayResponseModel, index: number): ParachuteItem => {
    return {
      id: `parachute-${index}-${Math.random().toString(36).substr(2, 9)}`,
      giveAwayData: giveAway,
      x: Math.random() * (isPhone(platform) ? 30 : 70) + 15,
      delay: Math.random() * 3,
      duration: 30,
      isClicked: false,
      swingOffset: (Math.random() - 0.5) * 60,
      promotionCode: giveAway.promotionCode,
    };
  };

  const stopConfetti = (itemId: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, showConfetti: false } : item)),
    );
  };

  const handleItemClick = (item: ParachuteItem) => {
    if (!isUserLoggedIn) {
      dispatch(onChangeAuthPage(0));
      return;
    }
    if (
      !currentUser?.kyc ||
      currentUser.kyc.status === 'INPROGRESS' ||
      currentUser.kyc.status === 'RETRY'
    ) {
      showConfirmModal({
        title: 'KYC Verification Required',
        content: <p>Please complete KYC to access this feature.</p>,
        width: 360,
        icon: <WarningIcon />,
        onOk: () => {
          if (onKYCRequired) {
            onKYCRequired();
          }
          setItems((prev) => prev.filter((i) => i.id !== item.id));
          navigate('/kyc');
          if (onKYCRequired) {
            onKYCRequired();
          }
        },
        onCancel: () => {},
        okText: 'Verify KYC',
        cancelText: 'Skip',
      });

      return;
    }

    if (item.isClicked) return;
    setItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, isClicked: true, showConfetti: true } : i)),
    );

    if (onItemClick && item.giveAwayData.promotionCode) {
      onItemClick(item.giveAwayData.promotionCode);
    }

    setTimeout(() => {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }, 3000);
  };

  useEffect(() => {
    if (!giveAwayData || giveAwayData.length === 0) return;

    giveAwayData.forEach((giveAway, index) => {
      setTimeout(
        () => {
          const newItem = createParachuteItem(giveAway, index);
          removeGiveAways(giveAway.promotionCode);

          setItems((prev) => [...prev, newItem]);

          setTimeout(
            () => {
              setItems((prev) => prev.filter((item) => item.id !== newItem.id));
            },
            (newItem.duration + newItem.delay) * 1000 + 5000,
          );
        },
        index * 500 + Math.random() * 300,
      );
    });
  }, [giveAwayData]);

  return (
    <>
      <div className="parachute-container">
        {items.map((item) => (
          <div
            key={item.id}
            onClick={() => handleItemClick(item)}
            className={`parachute-item ${item.isClicked ? 'clicked' : ''}`}
            style={{
              left: `${item.x}%`,
              animationDelay: `${item.delay}s`,
              animationDuration: `${item.duration}s`,
              animationName: `parachuteFall-${item.id}`,
              animationTimingFunction: 'linear',
              animationFillMode: 'forwards',
            }}
          >
            <img
              src={parachute}
              alt="parachute"
              className={`parachute-image ${item.isClicked ? 'clicked' : ''}`}
              style={{
                transform: `rotate(${Math.sin(Date.now() * 0.001 + item.x) * 8}deg)`,
              }}
            />
            {item.isClicked && <div className="celebration" />}
          </div>
        ))}

        {items.map(
          (item) =>
            item.showConfetti && (
              <ReactConfetti
                key={`confetti-${item.id}`}
                gravity={0.3}
                numberOfPieces={800}
                colors={[
                  '#FFD700',
                  '#FF69B4',
                  '#00BFFF',
                  '#7B68EE',
                  '#32CD32',
                  '#FF4500',
                  '#FF1493',
                  '#00CED1',
                ]}
                recycle={false}
                tweenDuration={7000}
                onConfettiComplete={() => stopConfetti(item.id)}
              />
            ),
        )}

        <style>{`
          ${items
            .map(
              (item) => `
            @keyframes parachuteFall-${item.id} {
              0% {
                top: -150px;
                opacity: 0;
                transform: translateX(0px) rotate(0deg);
              }
              10% {
                opacity: 1;
              }
              25% {
                transform: translateX(${item.swingOffset * 0.3}px) rotate(${
                  item.swingOffset * 0.1
                }deg);
              }
              50% {
                transform: translateX(${item.swingOffset * 0.7}px) rotate(${
                  item.swingOffset * 0.15
                }deg);
              }
              75% {
                transform: translateX(${item.swingOffset * 0.4}px) rotate(${
                  item.swingOffset * 0.1
                }deg);
              }
              100% {
                top: calc(100vh + 50px);
                opacity: 0.9;
                transform: translateX(${item.swingOffset}px) rotate(${item.swingOffset * 0.2}deg);
              }
            }
          `,
            )
            .join('\n')}
        `}</style>
      </div>
    </>
  );
};

export default ParachuteItems;
